import type { ProductMaterial } from '@rosita/shared';
import { compositionSum, type CompositionDraft } from './composition';

interface Props {
  materials: ProductMaterial[];
  value: CompositionDraft[];
  onChange: (next: CompositionDraft[]) => void;
}

const inputClass =
  'block w-20 rounded-md border border-admin-border bg-white px-2 py-1 text-sm text-right focus:border-admin-primary focus:outline-none';
const smallBtn =
  'flex h-7 w-7 items-center justify-center rounded-md border border-admin-border bg-white text-xs text-admin-text-soft hover:bg-admin-surface-alt disabled:opacity-40';

export default function ProductCompositionEditor({
  materials,
  value,
  onChange,
}: Props): JSX.Element {
  const selectedIds = new Set(value.map((v) => v.materialId));
  const visible = materials.filter((m) => m.isActive || selectedIds.has(m.id));
  const byId = new Map(materials.map((m) => [m.id, m]));

  const sum = compositionSum(value);
  const allFilled = value.length > 0 && value.every((v) => v.percentage !== '');
  const someMissing = value.some((v) => v.percentage === '');
  const overLimit = sum > 100;
  const offTarget = allFilled && !overLimit && sum !== 100;

  const toggle = (materialId: string): void => {
    if (selectedIds.has(materialId)) {
      onChange(value.filter((v) => v.materialId !== materialId));
    } else {
      onChange([...value, { materialId, percentage: '' }]);
    }
  };
  const setPercentage = (index: number, raw: string): void => {
    // Keep only digits; clamp to 100 so the field can't drift past the max.
    const digits = raw.replace(/\D/g, '');
    const clamped = digits === '' ? '' : String(Math.min(100, Number(digits)));
    onChange(value.map((v, i) => (i === index ? { ...v, percentage: clamped } : v)));
  };
  const move = (index: number, delta: number): void => {
    const target = index + delta;
    if (target < 0 || target >= value.length) return;
    const next = [...value];
    const [row] = next.splice(index, 1);
    next.splice(target, 0, row!);
    onChange(next);
  };

  if (materials.length === 0) {
    return (
      <p className="text-sm text-admin-muted">
        Sem materiais. Cria-os em Produtos &rsaquo; Configurações &rsaquo; Materiais.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <p className="-mt-2 text-sm text-admin-muted">
        Clica para adicionar materiais e indica a percentagem de cada um (ex.: 95% Algodão, 5%
        Elastano). Podes deixar a percentagem em branco.
      </p>
      <div className="flex flex-wrap gap-2">
        {visible.map((m) => {
          const selected = selectedIds.has(m.id);
          return (
            <label
              key={m.id}
              aria-pressed={selected}
              className={`inline-flex cursor-pointer select-none items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors ${
                selected
                  ? 'border-admin-primary bg-admin-primary text-white shadow-sm'
                  : 'border-dashed border-admin-border bg-white text-admin-text-soft hover:border-admin-primary hover:text-admin-primary'
              }`}
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={selected}
                onChange={() => toggle(m.id)}
              />
              <span aria-hidden="true" className="text-xs leading-none">
                {selected ? '✓' : '+'}
              </span>
              {m.name}
            </label>
          );
        })}
      </div>

      {value.length > 0 ? (
        <ul className="divide-y divide-admin-border rounded-md border border-admin-border bg-white">
          {value.map((row, i) => {
            const material = byId.get(row.materialId);
            return (
              <li key={row.materialId} className="flex items-center gap-3 px-3 py-2">
                <span className="flex-1 text-sm">{material?.name ?? '(material removido)'}</span>
                <label className="flex items-center gap-1 text-sm text-admin-text-soft">
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="—"
                    value={row.percentage}
                    onChange={(e) => setPercentage(i, e.target.value)}
                    aria-label={`Percentagem de ${material?.name ?? 'material'}`}
                    className={inputClass}
                  />
                  %
                </label>
                <button
                  type="button"
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  aria-label="Mover para cima"
                  className={smallBtn}
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => move(i, 1)}
                  disabled={i === value.length - 1}
                  aria-label="Mover para baixo"
                  className={smallBtn}
                >
                  ↓
                </button>
                <button
                  type="button"
                  onClick={() => toggle(row.materialId)}
                  aria-label={`Remover ${material?.name ?? 'material'}`}
                  className={`${smallBtn} text-admin-danger hover:bg-admin-danger hover:text-white`}
                >
                  ×
                </button>
              </li>
            );
          })}
          <li className="flex items-center justify-between px-3 py-2 text-sm">
            <span className="text-admin-text-soft">Soma</span>
            <span className={overLimit ? 'font-medium text-admin-danger' : 'font-medium'}>
              {sum}%
            </span>
          </li>
        </ul>
      ) : null}

      {overLimit ? (
        <p className="text-sm text-admin-danger">
          A soma das percentagens não pode exceder 100%.
        </p>
      ) : offTarget ? (
        <p className="text-sm text-amber-700">
          A soma é {sum}% — normalmente deve dar 100%. Confirma as percentagens antes de guardar.
        </p>
      ) : someMissing ? (
        <p className="text-sm text-admin-muted">
          Materiais sem percentagem aparecem na loja só com o nome.
        </p>
      ) : null}
    </div>
  );
}
